/* Hallmark · component: Stepper · genre: modern-minimal · theme: maeiz-tokens
 * Step progress indicator with completed, active, and upcoming states
 */
import React from "react";
import { Check } from "lucide-react";
import { clsx } from "clsx";

export interface StepItem {
    id: string | number;
    title: string;
    description?: string;
}

export interface StepperProps {
    steps: StepItem[];
    currentStep: number;
    onStepClick?: (stepIndex: number) => void;
    className?: string;
    autoScrollToTop?: boolean;
}

export const Stepper: React.FC<StepperProps> = ({
    steps,
    currentStep,
    onStepClick,
    className,
    autoScrollToTop = true,
}) => {
    React.useEffect(() => {
        if (autoScrollToTop && typeof window !== "undefined") {
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            document.documentElement.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            document.body.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            const main = document.querySelector("main");
            if (main && main.scrollTop > 0) {
                main.scrollTo({ top: 0, left: 0, behavior: "smooth" });
            }
        }
    }, [currentStep, autoScrollToTop]);

    return (
        <nav aria-label="Progress" className={clsx("w-full py-2", className)}>
            <ol role="list" className="flex items-center justify-between w-full">
                {steps.map((step, index) => {
                    const isCompleted = index < currentStep;
                    const isCurrent = index === currentStep;
                    const isUpcoming = index > currentStep;
                    const isClickable = Boolean(onStepClick && index <= currentStep);

                    return (
                        <li
                            key={step.id}
                            className={clsx(
                                "relative flex flex-1 items-center",
                                index === steps.length - 1 && "flex-none"
                            )}
                        >
                            <button
                                type="button"
                                disabled={!isClickable}
                                onClick={() => isClickable && onStepClick?.(index)}
                                className={clsx(
                                    "group flex items-center gap-3 text-start transition-all",
                                    !isClickable && "cursor-default"
                                )}
                                aria-current={isCurrent ? "step" : undefined}
                            >
                                {/* Step Badge */}
                                <span
                                    className={clsx(
                                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-300",
                                        isCompleted &&
                                            "bg-gold text-neutral-dark shadow-sm hover:brightness-105",
                                        isCurrent &&
                                            "bg-gold text-neutral-dark ring-4 ring-gold/25 shadow-mz-glow font-extrabold",
                                        isUpcoming &&
                                            "border-2 border-neutral-border/80 dark:border-white/15 bg-white/50 dark:bg-white/5 text-neutral-dark/50 dark:text-neutral-light/50"
                                    )}
                                >
                                    {isCompleted ? (
                                        <Check size={16} strokeWidth={2.5} className="text-neutral-dark" />
                                    ) : (
                                        <span>{index + 1}</span>
                                    )}
                                </span>

                                {/* Title & Description */}
                                <div className="hidden sm:block">
                                    <p
                                        className={clsx(
                                            "text-xs font-semibold uppercase tracking-wider transition-colors",
                                            isCurrent && "text-gold font-bold",
                                            isCompleted && "text-neutral-dark dark:text-white",
                                            isUpcoming && "text-neutral-dark/40 dark:text-neutral-light/40"
                                        )}
                                    >
                                        {step.title}
                                    </p>
                                    {step.description && (
                                        <p
                                            className={clsx(
                                                "text-[11px] truncate max-w-[140px]",
                                                isCurrent || isCompleted
                                                    ? "text-neutral-dark/70 dark:text-neutral-light/70"
                                                    : "text-neutral-dark/30 dark:text-neutral-light/30"
                                            )}
                                        >
                                            {step.description}
                                        </p>
                                    )}
                                </div>
                            </button>

                            {/* Connecting Line */}
                            {index < steps.length - 1 && (
                                <div className="ms-3 me-3 flex-1 h-[2px] rounded-full overflow-hidden bg-neutral-border/60 dark:bg-white/10">
                                    <div
                                        className={clsx(
                                            "h-full transition-all duration-500",
                                            index < currentStep ? "w-full bg-gold" : "w-0 bg-transparent"
                                        )}
                                    />
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};
